export class StringUtils {

    private static htmlEscapeHelperEl = document.createElement('div');

    static newUuid4(): string {
        return self.crypto.randomUUID();
    }

    static newUuid7(date: Date = new Date()): string {
        const unixMs = date.getTime();
        if (!Number.isFinite(unixMs) || unixMs < 0 || unixMs > 0xffffffffffff) {
            throw new Error('uuidv7 date must be between 0 and 281474976710655 milliseconds since Unix epoch');
        }

        const timestamp = BigInt(Math.floor(unixMs));
        const bytes = StringUtils.randomBytes(16);

        // RFC 9562: first 48 bits are Unix epoch milliseconds.
        bytes[0] = Number((timestamp >> 40n) & 0xffn);
        bytes[1] = Number((timestamp >> 32n) & 0xffn);
        bytes[2] = Number((timestamp >> 24n) & 0xffn);
        bytes[3] = Number((timestamp >> 16n) & 0xffn);
        bytes[4] = Number((timestamp >> 8n) & 0xffn);
        bytes[5] = Number(timestamp & 0xffn);

        // Set version (7) and variant (10xx).
        bytes[6] = (bytes[6] & 0x0f) | 0x70;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;

        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    private static randomBytes(length: number): Uint8Array {
        const bytes = new Uint8Array(length);
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            crypto.getRandomValues(bytes);
            return bytes;
        }

        for (let i = 0; i < length; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }

        return bytes;
    }

    static equalsIgnoreCase(a: string | null | undefined, b: string | null | undefined): boolean {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;
        return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
    }

    /**
     * Fills a template string with corresponding values from the given template variables object.
     * https://stackoverflow.com/questions/30003353/can-es6-template-literals-be-substituted-at-runtime-or-reused
     *
     * @param {string} templateString - The template string containing placeholders in template literal format (e.g., `${placeholder}`).
     * @param {Object} templateVars - An object containing the key-value pairs to replace the placeholders in the template string.
     * @return {string} The resulting string after all placeholders in the template have been replaced with corresponding values.
     */
    static fillTemplate(templateString: string, templateVars: any){
        const functionBody = "return `"+templateString +"`;";
        return new Function(functionBody).call(templateVars);
    }

    /**
     * Decodes a JWT token and returns the payload as a JSON object
     * @param token JWT token string (access token or ID token)
     * @returns Decoded payload as a JSON object or null if token is invalid
     */
    static jwtDecode(token: string): any {
        try {
            // JWT token consists of three parts: header.payload.signature
            const parts = token.split('.');

            if (parts.length !== 3) {
                console.error('Invalid JWT token format');
                return null;
            }

            // Decode the payload (second part)
            const payload = parts[1];
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding JWT token:', error);
            return null;
        }
    }

    static escapeHtml(unsafe: string | undefined | null): string {
        StringUtils.htmlEscapeHelperEl.textContent = unsafe ?? '';
        return StringUtils.htmlEscapeHelperEl.innerHTML;
    }

    static escapeJsString(unsafe: string | undefined | null): string {
        if (unsafe == null) return '""';
        // Escape backslash, double quote, control chars, and newlines
        return '"' + unsafe.replace(/[\\"\u0000-\u001F\u2028\u2029]/g, c => {
            switch (c) {
                case '\\': return '\\\\';
                case '"':  return '\\"';
                case '\b': return '\\b';
                case '\f': return '\\f';
                case '\n': return '\\n';
                case '\r': return '\\r';
                case '\t': return '\\t';
                case '\v': return '\\v';
                case '\0': return '\\0';
                case '\u2028': return '\\u2028';
                case '\u2029': return '\\u2029';
                default:
                    const code = c.charCodeAt(0);
                    return '\\x' + code.toString(16).padStart(2, '0');
            }
        }) + '"';
    }

    static compare(a: string | null | undefined, b: string | null | undefined): number {
        if (a == null && b == null) return 0;
        if (a == null) return -1;
        if (b == null) return 1;
        return a.localeCompare(b);
    }

    static toSnakeCase(str: string | null | undefined): string | null {
        if (str == null) return null;
        return str
            .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert underscore before uppercase letters
            .replace(/[\s-]+/g, '_') // Replace spaces and hyphens with underscores
            .toLowerCase(); // Convert to lowercase
    }

    private static RandomStringChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    static randomString(length: number): string {
        let result = '';
        const charsLen = StringUtils.RandomStringChars.length;
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        for (let i = 0; i < length; i++) {
            result += StringUtils.RandomStringChars[array[i] % charsLen];
        }
        return result;
    }

    static isNullOrEmpty(str: string | null | undefined): boolean {
        return str == null || str.trim().length === 0;
    }

    static includes(renewalStatus: string | undefined | null, text: string, ignoreCase: boolean = false): boolean {
        if (renewalStatus == null) return false;
        if (ignoreCase) {
            return renewalStatus.toLowerCase().includes(text.toLowerCase());
        } else {
            return renewalStatus.includes(text);
        }

    }

    static joinNotBlank(separator: string, ...args: (string | undefined | null)[]): string {
        return args
            .map(arg => arg?.trim())
            .filter(arg => arg && arg !== '')
            .join(separator);
    }
}

export function trimIndent(strings: TemplateStringsArray, ...values: any[]): string {
    // Interpolate the template literal
    let raw = strings[0];
    for (let i = 0; i < values.length; i++) {
        raw += String(values[i]) + strings[i + 1];
    }

    // Split into lines
    const lines = raw.replace(/\r\n/g, '\n').split('\n');

    // Remove leading/trailing blank lines
    if (lines.length && lines[0].trim() === '') lines.shift();
    if (lines.length && lines[lines.length - 1].trim() === '') lines.pop();

    // Find minimum indent (ignore empty lines)
    const indents = lines
        .filter(line => line.trim().length > 0)
        .map(line => line.match(/^(\s*)/)![1].length);

    const minIndent = indents.length > 0 ? Math.min(...indents) : 0;

    // Remove minIndent from each line
    return lines
        .map(line => line.slice(minIndent))
        .join('\n');
}


export function trimIndentN(level: number, endWithNewLine: boolean = true): (strings: TemplateStringsArray, ...values: any[]) => string {
    return (strings, ...values) => {
        const raw = trimIndent(strings, ...values);
        const indent = ' '.repeat(level);
        // Remove leading indentation based on the specified level
        const lines = raw.split('\n');
        const indentedLines = lines.map(line => indent + line);
        const body = indentedLines.join('\n');
        return endWithNewLine ? body + '\n' : body;
    };
}
